import Img from 'gatsby-image';
// @ts-ignore (no types available for 'gatsby-storyblok-image')
import { getFluidGatsbyImage } from 'gatsby-storyblok-image';
import React from 'react';

import * as FormStylesDr from '../../../_scss/modules/_forms.dr.module.scss';
import * as FormStylesHs from '../../../_scss/modules/_forms.hs.module.scss';
import { HeaderLiveSearchStoryblok } from '../../../_shared/interfaces/storyblok';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import HsLink from '../../_common/hs-link/hs-link';
import {
    getLinkSearchDatabases,
    getLinksSearchResult,
    getProductSearchDatabases,
    getProductsSearchResult,
} from './header-live-search.helper';
import * as HeaderLiveSearchStylesDr from './header-live-search.styles.dr.module.scss';
import * as HeaderLiveSearchStylesHs from './header-live-search.styles.hs.module.scss';
import { LSLinksSearchDatabase, LSProductsSearchDatabase, LSSearchResult } from './header-live-search.types';

const HeaderLiveSearch = ({ blok, storyblokCommon }: { blok: HeaderLiveSearchStoryblok; storyblokCommon: StoryblokCommon }) => {
    const [images, setImages] = React.useState<any[]>([]);
    const [searchResults, setSearchResults] = React.useState<LSSearchResult[]>([]);
    const [showResults, setShowResults] = React.useState(true);
    const HeaderLiveSearchStyles =
        storyblokCommon.configuration.content.theme === 'dr' ? HeaderLiveSearchStylesDr : HeaderLiveSearchStylesHs;
    const FormStyles = storyblokCommon.configuration.content.theme === 'dr' ? FormStylesDr : FormStylesHs;
    const [productsSearchDatabases, setProductsSearchDatabases] = React.useState<LSProductsSearchDatabase[]>([]);
    const [linksSearchDatabases, setLinksSearchDatabases] = React.useState<LSLinksSearchDatabase[]>([]);
    const [inputChange, setInputChange] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState('');
    const liveSearchRef = React.useRef<HTMLDivElement>(null);

    const initImageList = (): void => {
        const imageList = [];
        if (blok.background_mobile && blok.background_mobile.filename) {
            imageList.push({ ...getFluidGatsbyImage(blok.background_mobile.filename), media: '(max-width: 767px)' });
        }
        if (blok.background_desktop && blok.background_desktop.filename) {
            imageList.push({ ...getFluidGatsbyImage(blok.background_desktop.filename), media: '(min-width: 768px)' });
        }
        setImages(imageList);
    };

    const showResult = () => {
        setShowResults(true);
    };

    const hideResult = () => {
        setShowResults(false);
    };

    const closeLiveSearchResult = (): void => {
        hideResult();
    };

    const pressEscCheck = React.useCallback((event): void => {
        if (event.key === 'Escape') {
            closeLiveSearchResult();
        }
    }, []);

    const checkPageClick = (e: MouseEvent): void => {
        if (null !== liveSearchRef.current) {
            if (!liveSearchRef.current.contains(e.target as Node)) {
                closeLiveSearchResult();
            }
        }
    };

    const handleFocus = React.useCallback((event): void => {
        showResult();
        event.target.select();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        showResult();
        updateResult(e.target.value);
        setInputChange(true);

        setTimeout(() => {
            setInputChange(false);
        }, 800);
    };

    const updateResult = (searchValue: string): void => {
        setSearchInput(searchValue);
        const searchResultProducts = getProductsSearchResult(searchValue, productsSearchDatabases);
        const searchResultLinks = getLinksSearchResult(searchValue, linksSearchDatabases);
        setSearchResults(searchResultProducts.concat(searchResultLinks));
    };

    const handleClick = () => {
        if (searchInput !== '') {
            return;
        }
        showResult();
        updateResult('');
        setInputChange(true);

        setTimeout(() => {
            setInputChange(false);
        }, 800);
    };

    const makeBold = (value: string, keyword: string) => {
        const regExp = new RegExp(keyword, 'ig');
        const matchedValues = value.match(regExp);
        const result = value.replace(regExp, '<b>' + matchedValues?.pop() + '</b>');
        return result;
    };

    React.useEffect(() => {
        initImageList();
    }, [blok]);

    React.useEffect(() => {
        setProductsSearchDatabases(getProductSearchDatabases(blok.live_search_products ?? [], storyblokCommon));
        setLinksSearchDatabases(getLinkSearchDatabases(blok.live_search_links ?? []));

        if (typeof window !== 'undefined') {
            document.addEventListener('keydown', pressEscCheck, false);
            document.addEventListener('mousedown', checkPageClick);

            return () => {
                document.removeEventListener('keydown', pressEscCheck, false);
                document.removeEventListener('mousedown', checkPageClick);
            };
        }
    }, []);

    return (
        <div className={HeaderLiveSearchStyles.headerLiveSearch} style={{ backgroundColor: blok.background_color?.color ?? '' }}>
            {images.length && (
                <div className={HeaderLiveSearchStyles.imageWrapper}>
                    <Img fluid={images} alt={blok.background_desktop?.alt ?? ''} />

                    {blok.background_cover !== 'none' && (
                        <div className={[HeaderLiveSearchStyles.cover, HeaderLiveSearchStyles[blok.background_cover ?? '']].join(' ')} />
                    )}
                </div>
            )}

            {blok.image_slope && (
                <div className={HeaderLiveSearchStyles.slopeWrapper}>
                    <div className={HeaderLiveSearchStyles.slope} />
                </div>
            )}

            <div className={[HeaderLiveSearchStyles.inner, images.length ? HeaderLiveSearchStyles.innerWithImage : ''].join(' ')}>
                <div
                    style={{
                        paddingTop: (blok.padding_top ?? '0') + '%',
                        paddingRight: (blok.padding_right ?? '0') + '%',
                        paddingBottom: (blok.padding_bottom ?? '0') + '%',
                        paddingLeft: (blok.padding_left ?? '0') + '%',
                    }}
                >
                    {blok.headline && <h1 style={{ color: blok.text_color }}>{blok.headline}</h1>}
                    {blok.subheadline && <h2 style={{ color: blok.text_color }}>{blok.subheadline}</h2>}

                    <div className={HeaderLiveSearchStyles.liveSearch} ref={liveSearchRef}>
                        <div
                            className={[
                                FormStyles.formField,
                                HeaderLiveSearchStyles.liveSearchInput,
                                inputChange ? HeaderLiveSearchStyles.keyDown : '',
                            ].join(' ')}
                        >
                            <input
                                placeholder={blok.live_search_placeholder}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                onClick={handleClick}
                            />
                        </div>
                        {searchResults.length > 0 && showResults && (
                            <ul className={HeaderLiveSearchStyles.liveSearchResult}>
                                {searchResults.map((entry, index) => {
                                    return (
                                        <li key={index}>
                                            <h5>{entry.headline}</h5>
                                            <ul>
                                                {entry.results.map((e, i) => {
                                                    return (
                                                        <li key={i}>
                                                            <HsLink storyblokCommon={storyblokCommon} to={e.link}>
                                                                <span dangerouslySetInnerHTML={{ __html: makeBold(e.text, searchInput) }} />
                                                            </HsLink>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {searchInput.length > 0 && searchResults.length === 0 && showResults && (
                            <div className={HeaderLiveSearchStyles.liveSearchResult}>
                                <p>{blok.live_search_no_result}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeaderLiveSearch;
